import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Shield } from 'lucide-react';

const policySections = [
  {
    id: 'examination',
    title: 'Examination Policy',
    objective: 'Ensure integrity, fairness, and standardization in the examination process.',
    points: [
      'All candidates must pass a preliminary eligibility check before authorization to take the examination.',
      'Examinations shall be conducted in secure environments with strict adherence to identification verification procedures.',
      'Testing formats may include Written Examinations and Objective Structured Clinical Examinations (OSCE), each tailored to assess specific competencies.',
      'Examination content will be validated annually by subject matter experts to ensure relevance and adherence to the latest standards in medical practice.',
      'Candidates must receive their results within six weeks of examination completion.',
    ]
  },
  {
    id: 'examiner',
    title: 'Examiner Appointment',
    objective: 'Establish a standardized process for selecting qualified examiners.',
    points: [
      'Examiners must possess relevant qualifications in their field and hold an active medical license.',
      'A rigorous selection process shall be followed, including background checks, competency assessments, and interviews.',
      'Examiners will undergo an initial training period, focusing on examination standards, candidate interaction, and score calibration techniques.',
      'Regular calibration sessions will be scheduled to maintain scoring consistency across different examiners and ensure alignment with IMCAC standards.',
      'Examiners must comply with conflict of interest regulations and disclose any potential bias or relationship with candidates.',
    ]
  },
  {
    id: 'center',
    title: 'Center Accreditation',
    objective: 'Ensure that all examination venues meet IMCAC rigorous standards.',
    points: [
      'Examination centers must apply for accreditation by submitting an audit report demonstrating compliance with IMCAC operational standards.',
      'Criteria for accreditation include facility accessibility, security measures, and equipment adequacy for both written and OSCE assessments.',
      'Periodic audits (bi-annually) will be conducted by IMCAC representatives to verify continued compliance with the set standards.',
      'Centers must provide a conducive environment for candidates, including appropriate accommodations for individuals with disabilities.',
      'Any center exhibiting non-compliance or integrity concerns will face immediate investigation and, if necessary, revocation of their accreditation.',
    ]
  },
  {
    id: 'data',
    title: 'Data Protection',
    objective: 'Safeguard candidate information while ensuring compliance with relevant data protection regulations.',
    points: [
      'IMCAC will follow GDPR and local data privacy legislation to ensure all personal data is collected and processed securely.',
      'Candidates will have the right to access their data, request corrections, and request deletion following relevant guidelines.',
      'All electronic records must be encrypted, and access must be limited to authorized personnel only.',
      'Regular training sessions must be conducted for staff handling personal data to ensure compliance with data protection laws.',
    ]
  },
  {
    id: 'appeal',
    title: 'Appeal Mechanism',
    objective: 'Provide a clear and fair process for candidates who wish to contest examination results.',
    points: [
      'Candidates must submit a formal appeal within 14 days of receiving their examination results.',
      'Appeals must be reviewed by a committee independent of the examination process to ensure impartiality.',
      'A decision will be communicated within 30 days, outlining the findings and any actions taken regarding the appeal.',
      'If necessary, an opportunity for a personal hearing will be provided to the candidate as part of the appeal process.',
    ]
  },
  {
    id: 'cpd',
    title: 'CPD & Recertification',
    objective: 'Encourage lifelong learning and maintain high standards among certified professionals.',
    points: [
      'Recertification will be required every five years to ensure ongoing competence and up-to-date knowledge.',
      'Candidates must provide evidence of CPD activities, including participation in workshops, seminars, and peer-reviewed publications.',
      'A minimum number of CPD hours will be required each year, with specific activities pre-approved by IMCAC.',
      'Audit of CPD compliance will be conducted regularly to maintain the integrity of the certification process.',
    ]
  },
];

export default function PoliciesPage() {
  return (
    <div data-testid="policies-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Regulatory Framework</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Policies & Regulations</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <Tabs defaultValue="examination" className="w-full">
            <TabsList className="flex flex-wrap gap-1 h-auto bg-[#F1F5F9] p-1 mb-8" data-testid="policy-tabs">
              {policySections.map((p) => (
                <TabsTrigger key={p.id} value={p.id} className="text-xs px-3 py-2 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid={`policy-tab-${p.id}`}>
                  {p.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {policySections.map((policy) => (
              <TabsContent key={policy.id} value={policy.id}>
                <Card className="imcac-card" data-testid={`policy-content-${policy.id}`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className="h-6 w-6 text-[#C5A572]" />
                      <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading">{policy.title} Policy</h2>
                    </div>
                    <div className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-4 mb-6">
                      <p className="text-sm text-slate-700"><strong>Objective:</strong> {policy.objective}</p>
                    </div>
                    <ul className="space-y-4">
                      {policy.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-[#1B2A4A] rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-[#C5A572]">{i + 1}</span>
                          </span>
                          <p className="text-sm text-slate-700 leading-relaxed">{point}</p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
